import { Grid, GridColumn as Column, GridColumnMenuProps, GridColumnMenuCheckboxFilter, GridColumnMenuFilter, GridSelectionChangeEvent, GridCellProps, GridHeaderSelectionChangeEvent, GridPageChangeEvent, GridToolbar, GridColumn, GridFilterChangeEvent, GridColumnResizeEvent, GridColumnReorderEvent, GridDataStateChangeEvent, cellOperatorChange, cellInputChange, GridNoRecords } from '@progress/kendo-react-grid';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalization } from '@progress/kendo-react-intl';
import { Popup } from '@progress/kendo-react-popup';
import { Dialog } from "@progress/kendo-react-dialogs";

import { AccessToken } from '../config/AccessToken';
import { useNavigate } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { HeaderThElement, PagerTargetEvent } from '@progress/kendo-react-data-tools';
import { arrowRotateCwIcon, fileCsvIcon, fileExcelIcon, filePdfIcon, saveIcon, uploadIcon } from '@progress/kendo-svg-icons';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { GridPDFExport } from '@progress/kendo-react-pdf';

import { process as kendoProcess } from "@progress/kendo-data-query";
import { CSVLink } from 'react-csv';
import { json2csv } from 'json-2-csv';
import { Window } from "@progress/kendo-react-dialogs";
import { loadingPanel } from '../components/LoadingPanel';
import { ConfiguratorToggleColumnsGrid } from '../components/ConfiguratorToggleColumnsGrid';
import { DatePicker, TimePicker } from '@progress/kendo-react-dateinputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { InteractionRequiredAuthError } from '@azure/msal-browser';


interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };


const LinkMapCell = (props: any) => {

  const localizationService = useLocalization();
  const navigate = useNavigate()

  const mapId = props?.dataItem?.mapId
  const longitude = props?.dataItem?.info?.position?.longitude
  const latitude = props?.dataItem?.info?.position?.latitude
  const elevation = props?.dataItem?.info?.position?.elevation

  const showMapFromSelectedAlarm = () => {
    let alarmInfoSelected = props?.dataItem;
    localStorage.setItem("alarmInfoSelected", JSON.stringify(alarmInfoSelected));
    navigate("/home/map")
  }

  const isMapEnabled = !(mapId == null || (longitude == 0 && latitude == 0 && elevation == 0));
  const mapButtonTitle = isMapEnabled
    ? localizationService.toLanguageString('alarms.map', 'NOT_FOUND')
    : localizationService.toLanguageString('alarms.no_position_found', 'NOT_FOUND');

  return (
    <td className="k-command-cell" style={{ textAlign: 'center' }}>
      <button
        id="map"
        type="button"
        aria-label={mapButtonTitle}
        className="k-icon k-font-icon k-i-globe alarm-globe alarm-cell-action"
        style={{
          color: '#0f6671',
          margin: '6px',
          padding: '12px',
          borderRadius: '999px',
          border: 'none',
          background: '#e8f5f7',
          outline: 'none',
          cursor: isMapEnabled ? 'pointer' : 'not-allowed',
          opacity: isMapEnabled ? 1 : 0.4,
          boxShadow: isMapEnabled ? '0 2px 8px rgba(11, 74, 84, 0.2)' : 'none'
        }}
        title={mapButtonTitle}
        disabled={!isMapEnabled}
        onClick={isMapEnabled ? showMapFromSelectedAlarm : undefined}
      />
    </td>
  )
}

/**
 * Component to manage and display alarms in a grid format.
 * 
 * This component fetches and displays a list of alarms, allowing the user to
 * filter, sort, and paginate through the alarms. It also provides functionality
 * for exporting the data to Excel, CSV, and PDF formats, as well as saving and
 * loading the grid state configuration.
 * 
 * Methods:
 *  - getAlarmList: Fetches the list of alarms from the server.
 *  - pageChange: Handles page change events.
 *  - onSelectedItemsChange: Handles changes in selected items.
 *  - onHeaderSelection: Handles header selection changes.
 *  - ColumnMenuCheckboxFilter: Renders column menu with checkbox filters.
 *  - getInfoTagAlarm, toggleAlarm: Renders custom cell content for grid.
 *  - InfoPopupRender: Renders popup content for alarm info.
 *  - disableAlarm: Disables selected alarms.
 *  - saveStateToLocalStorage, loadStateFromLocalStorage: Save/load grid state.
 *  - setWidth: Calculates column widths.
 *  - excelExport, pdfExport: Handles data export to Excel and PDF.
 *  - onSearchChange: Handles changes in the search input.
 *  - onFilterChange: Handles filter change events.
 *  - onColumnReorder, onColumnResize: Handles column reorder/resize events.
 *  - onDataStateChange: Handles data state changes for paging and filtering.
**/

export const Alarms = (props?: any) => {
  const isPreview = Boolean(props?.preview);

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const [alarms, setAlarms] = useState<any>([]);
  const [alarmInfo, setAlarmInfo] = useState<any>([]);
  const [error, setError] = useState('');
  const [rowSelected, setRowSelected] = useState<any>([]);

  const [selectedState, setSelectedState] = useState<{ [id: string]: boolean | number[] }>({});

  const [pageIndex, setPageIndex] = useState<any>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState()

  const [disableAllAlarms, setDisableAllAlarms] = useState(false);

  const [columns, setColumns] = useState<any>([]);

  const [filterName, setFilterName] = useState<any>(() => {
    const filterNameSynoptic = localStorage.getItem('filterNameSynoptic');
    return filterNameSynoptic ? JSON.parse(filterNameSynoptic) : [];
  });

  const [showPopupInfo, setShowPopupInfo] = useState(false);

  const [loading, setLoading] = useState(false);

  const DATA_ITEM_KEY: string = 'id';

  const localizationService = useLocalization();

  const [instance, accessTokenRequest] = AccessToken();

  const userEmail = localStorage.getItem('email');

  const languageId = localStorage.getItem('languageId');

  const ColumnMenuCheckboxFilter = (props: GridColumnMenuProps) => {
    return (
      <div>
        <GridColumnMenuCheckboxFilter {...props} data={alarms} expanded={true} searchBoxFilterOperator={'contains'} />
      </div>
    );
  }

  const removeFilterByField = (filterObject: any, fieldName: any): any => {
    if (!filterObject || !filterObject.filters || !Array.isArray(filterObject.filters)) {
      return { logic: 'and', filters: [] };
    }

    const updatedFilters = filterObject.filters
      .map((item: any) => {
        // Se è il filtro da rimuovere
        if (item.field === fieldName) {
          return null;
        }

        // Se è un gruppo di filtri annidato
        if (item.filters && Array.isArray(item.filters)) {
          const updatedGroup = removeFilterByField(item, fieldName);
          // Mantieni solo se il gruppo ha ancora filtri
          if (updatedGroup.filters.length > 0) {
            return updatedGroup;
          } else {
            return null;
          }
        }
        return item;
      })
      .filter((item: any) => item !== null);

    // Sempre restituire un oggetto con 'logic' e 'filters'
    return {
      logic: filterObject.logic || 'and',
      filters: updatedFilters
    };
  };

  const GridColumnMenuFilterCell = (props: any) => {

    const gridColumnMenuFilterCell = useRef<any>(null);

    const handleResetClick = () => {
      const field = props.field;

      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;

      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };

      setDataState(newState);
      setFilterName((prev: any[]) => {
        const updated = prev?.filter((item) => item !== field) ?? [];
        localStorage.setItem('filterNameAlarm', JSON.stringify(updated));
        return updated;
      });

      localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };

    useEffect(() => {
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    const [selectedOperator, setSelectedOperator] = useState(props.operator || 'eq');

    const containsDateTimeField = (filters: any): boolean => {
      if (!Array.isArray(filters)) return false;

      return filters.some((filter: any) => {
        if (filter.field === 'dateTime') {
          return true;
        }
        // Se ha sotto-filtri, ricorsivamente cerca anche lì
        if (Array.isArray(filter.filters)) {
          return containsDateTimeField(filter.filters);
        }
        return false;
      });
    };

    const [tempDateTime, setTempDateTime] = useState<Date | any>(null);

    const handleTempDateTimeChange = (value: Date | null) => {
      setTempDateTime(value);
    };

    const operatorChange = (event: any) => {
      const newOperator = event.value.operator;
      setSelectedOperator(newOperator); // <-- Track selected operator
      cellOperatorChange(event, props.value, props.onChange);
    };

    const renderOperatorEditor = () => {
      const { operators } = props;

      const filteredOperators = operators.filter(
        (op: any) => op.operator !== 'isnull' && op.operator !== 'isnotnull'
      );

      const value = filteredOperators.find((item: any) => item.operator === selectedOperator) || null;

      return (
        <DropDownList
          value={value}
          onChange={operatorChange}
          data={filteredOperators}
          textField="text"
        />
      );
    };

    useEffect(() => {
      if (!tempDateTime) return;

      // Serve solo a far vedere a Kendo che c'è un valore
      props.onChange?.({
        field: props.field,
        operator: selectedOperator,
        value: new Date(tempDateTime.setMilliseconds(0))
      });
    }, [tempDateTime]);


    useEffect(() => {
      if (props.value && !tempDateTime) {
        setTempDateTime(new Date(props.value));
      }
    }, [props.value]);


    const applyDateTimeFilter = () => {
      if (!tempDateTime) return;

      const cleanDate = new Date(tempDateTime);
      cleanDate.setMilliseconds(0);

      const newFilter = {
        field: props.field,
        operator: selectedOperator,
        value: cleanDate,
      };

      const existingFilters = dataState?.filter?.filters || [];

      const updatedFilters = existingFilters.filter((f: any) => {
        if (f.filters) {
          return !f.filters.some((inner: any) => inner.field === props.field);
        }
        return f.field !== props.field;
      });

      updatedFilters.push({
        logic: 'or',
        filters: [newFilter],
      });

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilters,
        },
      };

      setDataState(newState);

      props.onChange?.({
        field: props.field,
        operator: selectedOperator,
        value: cleanDate,
      });

      let filterName: any = []
      updatedFilters?.forEach((filter: any) => {
        filter?.filters?.forEach((filterInfo: any) => {
          filterName.push(filterInfo.field)
        })
      })

      setFilterName(filterName)

      localStorage.setItem('filterNameAlarm', JSON.stringify(filterName));
      localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');

      getAlarmList();
    };

    useEffect(() => {
      const handleDocumentClick = (e: MouseEvent) => {
        const resetButton = (e.target as HTMLElement).closest('button[type="reset"]');
        const applyButton = (e.target as HTMLElement).closest('button.k-button');

        const resetText = resetButton?.textContent?.trim();
        const applyText = applyButton?.textContent?.trim();

        if (resetText === 'Ripulisci' || resetText === 'Clear') {
          handleResetClick();
        } else if (applyText === 'Filtro' || applyText === 'Filter') {
          applyDateTimeFilter();
        }
      };

      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, [tempDateTime, selectedOperator]);

    return (
      <div
        ref={gridColumnMenuFilterCell}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ zIndex: 10000, position: 'relative' }}
      >
        {renderOperatorEditor()}

        <DatePicker
          value={tempDateTime}
          format={languageId === 'en-US' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}
          formatPlaceholder="formatPattern"
          onChange={(e) => handleTempDateTimeChange(e.value)}
        />

        <div style={{ marginBottom: '10px' }}></div>

        <TimePicker
          value={tempDateTime}
          format={languageId === 'en-US' ? 'hh:mm:ss a' : 'HH:mm:ss'}
          formatPlaceholder="formatPattern"
          onChange={(e) => handleTempDateTimeChange(e.value)}
        />

      </div>
    );
  };

  const CustomGridFilterDateTime = (props: any) => {
    const {
      firstFilterProps
    } = props;


    return (
      <div>
        <GridColumnMenuFilterCell
          {...firstFilterProps}
        />
      </div>
    );
  };

  const InfoTagAlarmComponent = (props: any) => {
    const tagInfo = props.dataItem
    return (
      <td
        className="k-command-cell"
      >
        <button
          className="k-icon k-font-icon k-i-information"
          style={{
            color: '#0f6671',
            margin: '6px',
            padding: '12px',
            borderRadius: '999px',
            border: 'none',
            background: '#e8f5f7',
            outline: 'none',
            boxShadow: '0 2px 8px rgba(11, 74, 84, 0.2)',
            cursor: 'pointer'
          }}
          onClick={() => {
            setAlarmInfo(tagInfo);
            setShowPopupInfo(true);
          }
          }
        ></button>
      </td>
    )
  };

  const currColumns: any = [
    { field: 'deviceMac', title: localizationService.toLanguageString('alarms.deviceMac', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, minWidth: 100, width: props.preview ? '' : 250, filterable: false, show: true },
    { field: 'name', title: localizationService.toLanguageString('alarms.name', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, minWidth: 160, width: props.preview ? '' : 200, filterable: false, show: true },
    { field: 'zoneName', title: localizationService.toLanguageString('alarms.zoneName', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, minWidth: 160, width: props.preview ? '' : 200, filterable: false, show: true },
    { field: 'dateTime', title: localizationService.toLanguageString('alarms.dateTime', 'NOT_FOUND'), minWidth: 160, width: props.preview ? '' : 300, editable: false, filter: 'date', format: '{0:dd/MM/yyyy HH:mm:ss}', show: true },
    { field: 'eventType.eventTypeName', title: localizationService.toLanguageString('alarms.eventType.eventTypeName', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, minWidth: props.preview ? 200 : 160, width: props.preview ? '' : 300, filterable: false, editable: false, show: true },
    { field: 'eventType.eventTypeDescription', title: localizationService.toLanguageString('alarms.eventType.eventTypeDescription', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, minWidth: props.preview ? 450 : 400, width: 300, filterable: false, editable: false, show: true },
    { field: 'info', title: localizationService.toLanguageString('alarms.info', 'NOT_FOUND'), minWidth: 80, width: props.preview ? 0 : 80, cell: !props.preview ? InfoTagAlarmComponent : undefined, filterable: false, editable: false, show: props.preview ? false : true },
    { field: 'groupName', title: localizationService.toLanguageString('alarms.groupName', 'NOT_FOUND'), minWidth: 160, width: props.preview ? '0px' : 200, columnMenu: props.preview ? '' : ColumnMenuCheckboxFilter, filterable: false, editable: false, show: true }
  ];

  const deviceMacFromSynopticInAlarm = localStorage.getItem('deviceMacFromSynopticInAlarm');

  useEffect(() => {
    loadStateFromLocalStorage();
    if (deviceMacFromSynopticInAlarm != null) {
      const filterName: any = ['deviceMac']

      localStorage.setItem("filterNameAlarm", JSON.stringify(filterName));

      const initialFilter = {
        logic: "and",
        filters: [
          {
            logic: "or",
            filters: [
              {
                field: "deviceMac",
                operator: "eq",
                value: deviceMacFromSynopticInAlarm,
              }
            ]
          }
        ]
      };

      setDataState((prev: any) => ({
        ...prev,
        filter: initialFilter,
      }));

      const updatedState = {
        dataState: {
          ...dataState,
          filter: initialFilter,
          skip: 0,
          take: 10
        },
        columns: currColumns
      };

      localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify(updatedState));

      getAlarmListByDeviceMac(deviceMacFromSynopticInAlarm)
    } else {
      getAlarmList()
    }
  }, [])

  const getAlarmList = async (removedDeviceMacFromSynopticInAlarm?: any) => {

    setLoading(true);

    if (removedDeviceMacFromSynopticInAlarm) {

      const current = JSON.parse(localStorage.getItem('filterNameAlarm') || '[]');
      const updated = current.filter((item: any) => item !== 'deviceMac');
      localStorage.setItem('filterNameAlarm', JSON.stringify(updated));

      setDataState((prev: any) => {
        const currentFilter = prev.filter;

        if (!currentFilter || !Array.isArray(currentFilter.filters)) return prev;

        const newFilters = currentFilter.filters
          .map((group: any) => {
            if (group.logic === 'or' && Array.isArray(group.filters)) {
              // rimuove solo 'deviceMac' da OR
              const newSubFilters = group.filters.filter((f: any) => f.field !== 'deviceMac');
              return newSubFilters.length > 0 ? { ...group, filters: newSubFilters } : null;
            }
            // rimuove se è un filtro diretto su deviceMac
            return group.field === 'deviceMac' ? null : group;
          })
          .filter(Boolean);

        const newFilter = {
          ...currentFilter,
          filters: newFilters
        };

        const updatedState = {
          ...prev,
          filter: newFilter
        };

        // aggiorna anche localStorage
        localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify({
          dataState: {
            ...prev,
            filter: newFilter,
            skip: prev.skip ?? 0,
            take: prev.take ?? 10
          },
          columns: currColumns
        }));

        return updatedState;
      });

    }


    let languageId = localStorage.getItem('languageId');
    const apiLoadErrorMessage = localizationService.toLanguageString(
      'alarms.load_error',
      languageId === 'it-IT' ? 'Impossibile caricare gli allarmi' : 'Unable to load alarms'
    );
    setError('');

    const commonOperations = (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      const alarmListUrl = isPreview
        ? `${SERVER_URL}/alarm/list?light=true`
        : `${SERVER_URL}/alarm/list`;

      axios.get(alarmListUrl, { headers })
        .then((res: any) => {

          const data = res?.data?.data

          const alarmsInfo: any = []

          // format date in data.data
          data?.forEach((alarm: any) => {
            if (alarm.dateTime == null) {
              alarm.dateTime = localizationService.toLanguageString('alarms.date_not_found', 'NOT_FOUND')
              return
            }
            alarm.dateTime = convertDateTime(alarm?.dateTime)

            alarmsInfo.push(alarm)
          })

          setAlarms(alarmsInfo)

          loadStateFromLocalStorage();

          setLoading(false);

        }).catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
            return;
          }
          setAlarms([]);
          setError(apiLoadErrorMessage);
          loadStateFromLocalStorage();
          setLoading(false);
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  const getAlarmListByDeviceMac = async (deviceMac: any, skip?: any, take?: any) => {

    setLoading(true);

    let languageId = localStorage.getItem('languageId');
    const apiLoadErrorMessage = localizationService.toLanguageString(
      'alarms.load_error',
      languageId === 'it-IT' ? 'Impossibile caricare gli allarmi' : 'Unable to load alarms'
    );
    setError('');

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

      await axios.get(`${SERVER_URL}/alarm/ListAlarmsByMac?mac=${deviceMac}`, { headers })
        .then((res: any) => {

          const data = res?.data?.data
          if (data?.length == 0) {
            setAlarms([]);
            loadStateFromLocalStorage();
            setLoading(false);
            return
          }

          // format date in data.data
          data?.forEach((alarm: any) => {
            if (alarm.dateTime == null) {
              alarm.dateTime = localizationService.toLanguageString('alarms.date_not_found', 'NOT_FOUND')
              return
            }
            alarm.dateTime = alarm?.dateTime?.split('T')[0]

            //format alarm.dateTime with gg/mm/yyyy
            var dateParts = alarm.dateTime.split("/");

            var yearFormatted: any = dateParts[2].split(' ')[0]

            const day = dateParts[0] //date?.getDay();
            const month = dateParts[1] //(date.getMonth() + 1).toString().padStart(2, "0");
            const year = yearFormatted;
            alarm.dateTime = new Date(`${year}, ${month},${day}`)
          })

          setAlarms(data)

          loadStateFromLocalStorage();

          setLoading(false);

        }).catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
            return;
          }
          setAlarms([]);
          setError(apiLoadErrorMessage);
          loadStateFromLocalStorage();
          setLoading(false);
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  const exportRecords = async (format: any) => {

    setLoading(true);

    let languageId = localStorage.getItem('languageId');

    const commonOperations = (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

      axios.get(`${SERVER_URL}/alarm/download?format=${format}`, { headers, responseType: 'blob' })
        .then((response) => {

          const type: any = format == 'csv' ? 'text/csv;charset=utf-8;' : format == 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf'

          const ext: any = format == 'csv' ? '.csv' : format == 'excel' ? '.xlsx' : '.pdf'

          const blob = new Blob([response.data], { type: type });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.setAttribute("href", url);
          link.setAttribute("download", `records${ext}`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setLoading(false);

        }).catch((error) => {
          if (error.response) {
            setError(error.response.data.message);
          } else {
            setError(error.message);
          }
          setLoading(false);
        });
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  const onPageChange = (event: any) => {
    const targetEvent = event.targetEvent;
    const newSkip = event?.page?.skip;
    const newTake = event?.targetEvent?.value === 'All' ? alarms?.length : event.page.take;
    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setDataState((prevState: any) => ({
      ...prevState,
      skip: newSkip,
      take: newTake
    }));
    // Salva lo stato aggiornato completo nel localStorage
    const updatedState = {
      dataState: {
        ...dataState,
        skip: newSkip,
        take: newTake
      },
      columns
    };
    localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify(updatedState));
  };

  // used for checkbox selection
  const onSelectedItemsChange = useCallback((event: GridSelectionChangeEvent) => {
    setRowSelected(event?.dataItems?.map((item: any) => item.id) || [])

    if (event?.syntheticEvent?.currentTarget?.checked) {
      props.handlePopupAlarm()
    }
  },
    [selectedState]
  );

  const onHeaderSelection = useCallback((event: GridHeaderSelectionChangeEvent) => {

    const newSelectedState: any = [];

    let data: any = event.dataItems ?? event

    data?.data?.forEach((item: any) => {
      newSelectedState.push(item.id)
    });
    setSelectedState(newSelectedState);
    setRowSelected(newSelectedState)
    props.handlePopupAlarm()
  },
    []
  );

  const toggleAlarm = (propsGrid: GridCellProps) => {

    return (
      <td className="k-command-cell" style={{ textAlign: 'center' }}>
        <button
          id='disabled-alarm'
          type='button'
          className="k-icon k-font-icon k-i-bell alarm-cell-action"
          style={{
            color: '#0f6671',
            margin: '6px',
            padding: '12px',
            borderRadius: '999px',
            border: 'none',
            background: '#e8f5f7',
            outline: 'none',
            boxShadow: '0 2px 8px rgba(11, 74, 84, 0.2)',
            cursor: 'pointer'
          }}
          onClick={() => {
            setRowSelected(propsGrid.dataItem.id)
            setDisableAllAlarms(false)
            props.handlePopupAlarm()
          }
          }
        >
          <a id='disabled-alarm' />
        </button>
      </td>
    )
  };

  const disableAlarm = async () => {

    const selectedCount = Array.isArray(rowSelected) ? rowSelected.length : 1;
    const currentPageCount = Array.isArray(processedData?.data) ? processedData.data.length : 0;
    const shouldGoPrevPage = dataState?.skip > 0 && currentPageCount > 0 && selectedCount >= currentPageCount;

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}` };
      const params = {
        alarmIds: Array.isArray(rowSelected) ? rowSelected : [rowSelected],
      };
      try {
        await axios.post(`${SERVER_URL}/alarm/ackalarm`, params, { headers })
        if (shouldGoPrevPage) {
          const newSkip = Math.max(0, (dataState?.skip ?? 0) - (dataState?.take ?? 10));
          const updatedDataState = {
            ...dataState,
            skip: newSkip,
          };
          setDataState(updatedDataState);
          localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify({
            dataState: updatedDataState,
            columns
          }));
        }
        props.handlePopupAlarm()
        getAlarmList()
        loadStateFromLocalStorage();
      } catch (error: any) {
        setError(error);
      }
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken)
      })
    }
  }

  const disableAlarms = async () => {

    const selectedCount = Array.isArray(rowSelected) ? rowSelected.length : 1;
    const currentPageCount = Array.isArray(processedData?.data) ? processedData.data.length : 0;
    const shouldGoPrevPage = dataState?.skip > 0 && currentPageCount > 0 && selectedCount >= currentPageCount;

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}` };

      try {
        await axios.post(`${SERVER_URL}/alarm/ackalarms`, { headers })
        if (shouldGoPrevPage) {
          const newSkip = Math.max(0, (dataState?.skip ?? 0) - (dataState?.take ?? 10));
          const updatedDataState = {
            ...dataState,
            skip: newSkip,
          };
          setDataState(updatedDataState);
          localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify({
            dataState: updatedDataState,
            columns
          }));
        }
        props.handlePopupAlarm()
        getAlarmList()
        loadStateFromLocalStorage();
      } catch (error: any) {
        setError(error);
      }
    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken)
      })
    }
  }

  const [dataState, setDataState] = useState<any>({
    skip: 0,
    take: pageIndex?.take ?? 10,
    data: alarms,
    total: alarms?.length,
    filter: { logic: 'and', filters: [] },
    //sort: [{ field: 'mac', dir: 'desc' }]
  });

  const filteredGridFields = useMemo(() => {
    const fields = new Set<string>();

    const collectFields = (filter: any): void => {
      if (!filter || !Array.isArray(filter.filters)) {
        return;
      }

      filter.filters.forEach((entry: any) => {
        if (entry && Array.isArray(entry.filters)) {
          collectFields(entry);
          return;
        }

        const field = typeof entry?.field === 'string' ? entry.field.trim() : '';
        if (field.length > 0) {
          fields.add(field);
        }
      });
    };

    collectFields(dataState?.filter);
    return Array.from(fields);
  }, [dataState?.filter]);

  const hasActiveGridFilters = filteredGridFields.length > 0;

  const saveStateToLocalStorage = () => {
    const state: any = { dataState, columns };
    localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify(state));
  };

  const loadStateFromLocalStorage = () => {
    const storedFilter = localStorage.getItem(`gridAlarmState: ${userEmail}`);
    if (storedFilter) {
      const { dataState: savedDataState, columns: savedColumns } = JSON.parse(storedFilter);

      savedColumns.forEach((column: any) => {
        column.title = localizationService.toLanguageString(`alarms.${column.field}`, 'NOT_FOUND');
      })

      if (savedDataState?.filter != null) {
        // Funzione ricorsiva per gestire qualsiasi struttura di filtri
        const updateFilterValues = (filter: any): any => {
          if (Array.isArray(filter.filters)) {
            return {
              ...filter,
              filters: filter.filters.map(updateFilterValues),
            };
          } else {
            const field = filter?.field;
            let value = filter?.value;

            if (['dateTime'].includes(field)) {
              value = new Date(value);
            }

            return {
              ...filter,
              value,
            };
          }
        };

        const updatedFilter = updateFilterValues(savedDataState.filter);

        const updatedSavedDataState = {
          ...savedDataState,
          filter: updatedFilter,
        };

        setDataState(updatedSavedDataState);
      } else {
        setDataState(savedDataState);
      }

      const currentColumn: any = savedColumns?.map((column: any, id: any) => {
        if (column.id == id) {
          return { ...column, show: !column.show }; // Create a *new* object
        }
        return column; // Return the other columns unchanged
      });

      setColumns(currentColumn);

      return storedFilter
        ? storedFilter
        : currentColumn;
    } else {
      setColumns(currColumns)
    }
    setLoading(false);
  };

  const anchor = useRef<any>(null);
  const [showPopupSaveConfig, setShowPopupSaveConfig] = useState(false);

  const onClickSaveConfig = () => {
    setShowPopupSaveConfig(!showPopupSaveConfig);

    setTimeout(() => {
      setShowPopupSaveConfig(showPopupSaveConfig);
    }, 3000)

  };


  const CellRender = (props: any) => {
    return (
      <td
        {...props.tdProps}
        colSpan={1}
      >
        {props.children}
      </td>
    );
  }

  const onFilterChange = (event: GridFilterChangeEvent) => {
    const newFilter: any = event.filter
      ? { ...event.filter }
      : { logic: 'and', filters: [] };

    setDataState((prevState: any) => ({
      ...prevState,
      filter: newFilter
    }));

    let filterName: any = []
    newFilter?.filters?.forEach((filter: any) => {
      filter?.filters?.forEach((filterInfo: any) => {
        filterName.push(filterInfo.field)
      })
    })
    localStorage.setItem("filterNameAlarm", JSON.stringify(filterName));
  };

  const anchorSave = useRef<any>(null);

  const onChange = (event: any) => {
    const field = event.target.name;
    const newColumns = columns.map((column: any) => {
      if (column.field === field) {
        return { ...column, show: !column.show };
      }
      return column;
    });
    setColumns(newColumns);

    const state = { dataState, columns: newColumns };
    localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify(state));
  };

  const onColumnReorder = (event: GridColumnReorderEvent) => {
    setColumns(event.columns);
    saveStateToLocalStorage();
  };

  const onColumnResize = (event: GridColumnResizeEvent) => {
    setColumns(event.columns);
    saveStateToLocalStorage();
  };

  const sanitizeFilter = (filter: any) => {
    if (filter && typeof filter === 'object' && Array.isArray(filter.filters)) {
      const flatFilters = filter.filters.flatMap((f: any) =>
        Array.isArray(f.filters) ? f.filters : [f]
      );

      return {
        logic: filter.logic || 'and',
        filters: flatFilters
      };
    }
    return { logic: 'and', filters: [] };
  };

  const safeFilter = sanitizeFilter(dataState?.filter);
  const processedData: any = kendoProcess(alarms, {
    filter: dataState?.filter ?? safeFilter,
    sort: dataState?.sort,
    skip: dataState?.skip,
    take: dataState?.take,
  });

  const onDataStateChange = (event: any) => {

    const newFilter: any = event.dataState.filter
      ? { ...event.dataState.filter }
      : { logic: 'and', filters: [] };

    let filterName: any = []
    newFilter?.filters?.forEach((filter: any) => {
      filter?.filters?.forEach((filterInfo: any) => {
        filterName.push(filterInfo.field)
      })
    })
    setFilterName(filterName)
    localStorage.setItem("filterNameAlarm", JSON.stringify(filterName));

    const { dataState } = event;
    if (dataState) {
      const { filter, sort, skip, take } = dataState;
      const updatedFilter = filter || dataState.filter;
      const updatedSkip = skip !== undefined ? skip : dataState.skip;
      const updatedTake = take !== undefined ? take : dataState.take;
      setDataState((prevState: any) => ({
        ...prevState,
        filter: updatedFilter,
        sort: sort,
        skip: updatedSkip,
        take: updatedTake
      }));
    }
    const state = { dataState, columns };
    localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify(state));
  };

  const formatDateFromIsoToString = (date: any) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    if (languageId == 'en-US') {
      hours = hours % 12;
      hours = hours === 0 ? 12 : hours; // convert 0 → 12
      const hours12 = String(hours).padStart(2, '0');
      return `${month}/${day}/${year} ${hours12}:${minutes}:${seconds}`;
    } else {
      const hours24 = String(hours).padStart(2, '0');
      return `${day}/${month}/${year} ${hours24}:${minutes}:${seconds}`;
    }
  }

  const convertDateTime = (dateTime: any) => {

    const [datePart, timePart] = dateTime.split(' ');
    const [day, month, year] = datePart.split('/');
    const [hour, minute, seconds] = timePart.split(':');

    const date = new Date(Date.UTC(year, month - 1, day, hour, minute, seconds))

    return date
  }

  const DateTimeCellWithBackGroundComponent = (props: any) => {
    const date = new Date(props?.dataItem?.dateTime);
    const meridian = date.toLocaleString('en-US', { hour: 'numeric', hour12: true }).split(' ')[1];
    const dateTimeValue: any = formatDateFromIsoToString(props.dataItem.dateTime);
    if (dateTimeValue != null && !dateTimeValue.includes('1970')) {
      return (
        <td>
          <span style={{ display: 'block', width: '200px' }}>
            {dateTimeValue} {languageId === 'it-IT' ? '' : meridian}
          </span>
        </td>
      )
    } else {
      return (
        <td><span /></td>
      )
    }
  };

  const IconHeaderCell = (props: any) => {
    const field = typeof props?.field === 'string' ? props.field : '';
    const isFilteredColumn = field !== '' && filteredGridFields.includes(field);
    const mergedThProps = {
      ...(props?.thProps ?? {}),
      className: `${props?.thProps?.className ?? ''}${isFilteredColumn ? ' alarm-filtered-header' : ''}`.trim()
    };

    return (
      <HeaderThElement columnId={props.thProps?.columnId || ''} {...mergedThProps}>
        {props.children}
      </HeaderThElement>
    );
  };

  const alarmSurfaceStyle = {
    position: 'relative' as const,
    display: isPreview ? 'grid' as const : 'flex' as const,
    flexDirection: isPreview ? undefined : 'column' as const,
    width: isPreview ? '100%' : 'calc(100% - 20px)',
    height: isPreview
      ? '730px'
      : 'calc(100% - 20px)',
    minHeight: isPreview ? undefined : 0,
    overflow: isPreview ? 'visible' as const : 'hidden' as const,
    boxSizing: isPreview ? undefined : 'border-box' as const,
    margin: isPreview ? '0' : '10px',
    borderRadius: isPreview ? '0' : '12px',
    background: isPreview ? 'transparent' : 'linear-gradient(180deg, #f5fbfc 0%, #eaf5f7 100%)',
    border: isPreview ? 'none' : '1px solid #bdd8dd',
    boxShadow: isPreview ? 'none' : '0 14px 30px rgba(9, 64, 72, 0.2)'
  };

  const toolbarStyle = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    height: '75px',
    alignItems: 'center',
    borderRadius: '10px 10px 0px 0px',
    background: 'linear-gradient(180deg, #d8edf1 0%, #cfe8ed 100%)',
    border: 'none',
    borderBottom: '1px solid #9ec8cf'
  };

  const toolbarActionsStyle = {
    marginLeft: 'auto',
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    padding: '4px',
    borderRadius: '10px',
    backgroundColor: '#ecf7f9'
  };

  const toolbarButtonStyle = {
    fontWeight: 700,
    boxShadow: '0 2px 8px rgba(10, 74, 84, 0.18)'
  };

  const toolbarLeadStyle = {
    minWidth: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const toolbarContextStyle = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    gap: '10px',
    marginLeft: '12px'
  };

  const toolbarMacPillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    height: '30px',
    borderRadius: '999px',
    border: '1px solid #9fc5cc',
    background: '#edf7f9',
    color: '#1A8894',
    padding: '0 10px'
  };

  const noVisibleColumnsPanelStyle = {
    textAlign: 'center' as const,
    padding: '24px',
    border: '1px dashed #9ec8cf',
    borderRadius: '0 0 12px 12px',
    background: 'linear-gradient(180deg, #f7fcfd 0%, #edf7f9 100%)'
  };

  const gridWrapperStyle = {
    position: 'relative' as const
  };

  const onRefreshGrid = () => {
    const selectedMac = localStorage.getItem('deviceMacFromSynopticInAlarm');
    if (selectedMac) {
      getAlarmListByDeviceMac(selectedMac);
      return;
    }
    getAlarmList();
  };

  const alarmMotionCss = `
    @keyframes alarmFadeUp {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes alarmPopIn {
      from { opacity: 0; transform: scale(0.98); }
      to { opacity: 1; transform: scale(1); }
    }

    .alarm-animated-surface {
      animation: alarmFadeUp 260ms ease-out both;
      height: 97%;
    }

    .alarm-animated-surface.alarm-compact-surface {
      height: auto;
      min-height: 0;
      overflow: visible;
    }

    .drawer-layout-shell:has(.alarm-full-surface) {
      height: calc(100vh - 132px);
      min-height: 0;
    }

    .drawer-content-shell:has(.alarm-full-surface) {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }

    .drawer-content-shell:has(.alarm-full-surface) > .alarm-full-surface {
      flex: 0 0 auto;
      min-height: 0;
    }

    .alarm-full-surface .alarm-grid-colorized {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      overflow: hidden;
      box-sizing: border-box;
    }

    .alarm-full-surface .alarm-grid-colorized .k-grid-toolbar,
    .alarm-full-surface .alarm-grid-colorized .k-grid-header,
    .alarm-full-surface .alarm-grid-colorized .k-grid-pager {
      flex: 0 0 auto;
    }

    .alarm-full-surface .alarm-grid-colorized .k-grid-container {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      overflow: hidden;
    }

    .alarm-full-surface .alarm-grid-colorized .k-grid-aria-root {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
    }

    .alarm-full-surface .alarm-grid-colorized .k-grid-content {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
    }

    .alarm-animated-toolbar {
      animation: alarmPopIn 320ms ease-out both;
    }

    .alarm-animated-toolbar-secondary {
      animation-delay: 80ms;
    }

    .alarm-toolbar-actions {
      transition: box-shadow 200ms ease;
    }

    .alarm-toolbar-actions:hover {
      box-shadow: inset 0 0 0 1px rgba(13, 90, 102, 0.16);
    }

    .alarm-action-btn {
      transition: transform 170ms ease, box-shadow 170ms ease, filter 170ms ease;
    }

    .alarm-action-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 14px rgba(8, 65, 73, 0.24);
      filter: saturate(1.05);
    }

    .alarm-action-btn:focus-visible,
    .alarm-cell-action:focus-visible {
      outline: 2px solid #0f6671;
      outline-offset: 2px;
    }

    .alarm-cell-action {
      transition: transform 170ms ease, box-shadow 170ms ease, background-color 170ms ease;
    }

    .alarm-cell-action:hover {
      transform: scale(1.3);
      box-shadow: 0 4px 12px rgba(11, 74, 84, 0.26);
      background-color: #d9eef2 !important;
    }

    .alarm-cell-action:disabled,
    .alarm-cell-action:disabled:hover {
      transform: none !important;
      box-shadow: none !important;
      background-color: #edf4f5 !important;
    }

    .k-i-information:focus-visible {
      outline: 2px solid #0f6671;
      outline-offset: 2px;
    }

    .k-i-information {
      transition: transform 170ms ease, box-shadow 170ms ease, background-color 170ms ease;
    }

    .k-i-information:hover {
      transform: scale(1.3);
      box-shadow: 0 4px 12px rgba(11, 74, 84, 0.26);
      background-color: #d9eef2 !important;
    }

    .k-grid.k-grid-md.alarm-grid-colorized { 
      border-radius: 10px 10px 0 0;
    }

    .alarm-grid-colorized .k-table-tbody > tr > td {
      background: linear-gradient(180deg, rgba(250, 254, 255, 0.98) 0%, rgba(240, 250, 252, 0.98) 100%);
      border-bottom: 1px solid #c5dee3;
      border-right: 1px solid #d6e9ed;
      color: #114f58;
      padding: 0.56rem 0.62rem;
      vertical-align: middle;
      transition: background-color 170ms ease, box-shadow 170ms ease;
    }

    .alarm-grid-colorized .k-table-tbody > tr:nth-child(odd) > td {
      background: linear-gradient(180deg, rgba(251, 255, 255, 0.99) 0%, rgba(242, 251, 253, 0.99) 100%);
    }

    .alarm-grid-colorized .k-grid-toolbar {
      width: calc(100% + 2px);
      max-width: none;
      box-sizing: border-box;
      border-radius: 0 !important;
      border-left: none !important;
      border-right: none !important;
      margin: 0 -1px;
    }

    .alarm-grid-colorized .k-table-tbody > tr:nth-child(even) > td {
      background: linear-gradient(180deg, rgba(234, 247, 250, 0.98) 0%, rgba(223, 241, 245, 0.98) 100%);
    }

    .alarm-grid-colorized .k-table-tbody > tr:hover > td {
      background: linear-gradient(180deg, #e4f4f7 0%, #d7edf1 100%) !important;
      box-shadow: inset 0 0 0 1px rgba(10, 85, 97, 0.14);
    }

    .alarm-grid-colorized .k-grid-header th {
      background: linear-gradient(180deg, #d4eaf0 0%, #c7e1e8 100%);
      color: #0d4a54;
      border-bottom: 1px solid #9ec5cc;
      border-right: 1px solid #b7d6dc;
      padding: 0.62rem 0.62rem;
    }

    .alarm-grid-colorized .k-grid-header .k-sort-icon,
    .alarm-grid-colorized .k-grid-header .k-svg-icon {
      color: #0f6671;
    }

    .alarm-grid-colorized .alarm-filtered-header .k-grid-header-menu.k-grid-column-menu {
      background: #D4EBEF !important;
      border-radius: 4px;
    }

    .alarm-grid-colorized .k-grid-norecords td {
      padding: 0 !important;
      border-bottom: 1px solid #c5dee3;
    }

    .alarm-grid-colorized .alarm-no-records {
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      letter-spacing: 0.01em;
      color: #0f6671;
      background: linear-gradient(180deg, rgba(246, 252, 253, 0.95) 0%, rgba(235, 246, 248, 0.95) 100%);
    }

    @media (prefers-reduced-motion: reduce) {
      .alarm-animated-surface,
      .alarm-animated-toolbar,
      .alarm-animated-toolbar-secondary,
      .alarm-toolbar-actions,
      .alarm-action-btn,
      .alarm-cell-action {
        animation: none !important;
        transition: none !important;
      }
    }

    @media (max-width: 1550px) {
      .alarm-animated-surface {
        animation: alarmFadeUp 260ms ease-out both;
        height: 96%;
      }
    }
  `;


  const isAlarmGridEmpty = !loading && (processedData?.data?.length ?? 0) === 0;

  const noAlarmLabel = localizationService.toLanguageString('alarms.no_alarms_present', 'Nessuna allarme presente');
  const noRecordsLabel = error || noAlarmLabel;
  const hasVisibleDataColumns = Array.isArray(columns)
    ? columns.some((column: any) => column?.show)
    : columns?.columns?.some((column: any) => column?.show) ?? false;

  const _excelExport = useRef<ExcelExport | null>(null);
    const excelExport = () => {
      if (_excelExport.current !== null) {
        _excelExport.current.save();
      }
    };

  return (
    <div
      style={alarmSurfaceStyle}
      className={`alarm-animated-surface${isPreview ? '' : ' alarm-full-surface'}${isAlarmGridEmpty ? ' alarm-compact-surface' : ''}`}
    >
      <style>{alarmMotionCss}</style>

      {loading && loadingPanel}

      {!hasVisibleDataColumns ?
        <>
          <div style={toolbarStyle} className='alarm-animated-toolbar'>
            <div style={toolbarLeadStyle}>
                        <h3 className="page-title">{localizationService.toLanguageString('alarms.title', 'NOT_FOUND')}</h3>
            </div>
            <div style={toolbarActionsStyle} className='alarm-toolbar-actions'>
              <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />
              <Button
                themeColor={"primary"}
                size="small"
                title={localizationService.toLanguageString('export_to_csv', 'NOT_FOUND')}
                svgIcon={fileCsvIcon}
                style={toolbarButtonStyle}
                className='alarm-action-btn'
                disabled={true}
              />
              <Button
                themeColor={"primary"}
                size="small"
                title={localizationService.toLanguageString('export_to_pdf', 'NOT_FOUND')}
                svgIcon={filePdfIcon}
                style={toolbarButtonStyle}
                className='alarm-action-btn'
                disabled={true}
              />
              <Button
                title={localizationService.toLanguageString('export_to_excel', 'NOT_FOUND')}
                themeColor={"primary"}
                size="small"
                svgIcon={fileExcelIcon}
                style={toolbarButtonStyle}
                className='alarm-action-btn'
                disabled={true}
              />
            </div>
          </div>
          <div style={noVisibleColumnsPanelStyle}>
            <p>{localizationService.toLanguageString('alarms.no_visible_columns', 'No visible columns')}</p>
          </div>
        </>
        :
        <>
          <ExcelExport data={processedData} ref={_excelExport}>
            {columns
              .map((col: any) => (
                <ExcelExportColumn
                  key={col.field}
                  field={col.field}
                  title={localizationService.toLanguageString(`alarms.${col.field}`, col.title)}
                />
              ))}
          </ExcelExport>
          {/*<ExcelExport data={processedData}>
            <div style={gridWrapperStyle}>*/}
          <Grid
            className={`alarm-grid-colorized${hasActiveGridFilters ? ' grid-filter-active' : ''}`}
            data={processedData}
            dataItemKey={'id'}
            cells={{ data: CellRender }}
            sortable={true}
            reorderable={false}
            take={dataState?.take ?? 10}
            skip={dataState?.skip ?? 0}
            pageable={{
              type: 'input',
              buttonCount: 5,
              pageSizes: [10, 20, 50, 100, 'All'],
              pageSizeValue: pageSizeValue,
              info: true
            }}
            sort={dataState.sort}
            filter={dataState.filter}
            onSelectionChange={onSelectedItemsChange}
            onHeaderSelectionChange={onHeaderSelection}
            onPageChange={onPageChange}
            onColumnReorder={onColumnReorder}
            onColumnResize={onColumnResize}
            onDataStateChange={onDataStateChange}
            scrollable={'none'}
          >

            <GridNoRecords>
              <div className='alarm-no-records'>{noRecordsLabel}</div>
            </GridNoRecords>

            <GridToolbar style={toolbarStyle} className='alarm-animated-toolbar'>
              <div style={toolbarLeadStyle}>
                            <h3 className="page-title">{localizationService.toLanguageString('alarms.title', 'NOT_FOUND')}</h3>
              </div>
              {!props.preview && (
                <div style={toolbarContextStyle}>
                  <button
                    className='alarm-page disable-alarm-button'
                    onClick={() => {
                      onHeaderSelection(processedData);
                      setDisableAllAlarms(true)
                    }}
                  >
                    {localizationService.toLanguageString('alarms.disable_all_alarms_button', 'NOT_FOUND')}
                  </button>
                  {deviceMacFromSynopticInAlarm && (
                    <div style={toolbarMacPillStyle}>
                      <span style={{ fontSize: '14px', fontWeight: 700 }}>Mac:</span>
                      <span style={{ fontSize: '14px' }}>{deviceMacFromSynopticInAlarm}</span>
                      <Button
                        className='alarmsFilterByMac'
                        onClick={(e: any) => {
                          e.stopPropagation()
                          localStorage.removeItem('deviceMacFromSynopticInAlarm');
                          getAlarmList(true);
                        }}
                      >X</Button>
                    </div>
                  )}
                </div>
              )}

              <Popup
                anchor={anchor.current && anchor.current.element}
                show={showPopupSaveConfig}
                popupClass={'popup-content-save-synoptic-config'}
              >
                {localizationService.toLanguageString('synoptic_devices.saved_config', 'NOT_FOUND')}
              </Popup>

              <div style={toolbarActionsStyle} className='alarm-toolbar-actions'>
                <div>
                  <Button
                    type="button"
                    rounded="full"
                    fillMode="solid"
                    themeColor="primary"
                    size="small"
                    svgIcon={arrowRotateCwIcon}
                    title={localizationService.toLanguageString('alarms.refresh', 'Refresh grid')}
                    aria-label={localizationService.toLanguageString('alarms.refresh', 'Refresh grid')}
                    onClick={onRefreshGrid}
                    style={toolbarButtonStyle}
                    className='alarm-action-btn'
                    disabled={loading}
                  />
                </div>
                <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />

                <div>
                  <Button
                    themeColor={'primary'}
                    size="small"
                    title={localizationService.toLanguageString('export_to_csv', 'NOT_FOUND')}
                    svgIcon={fileCsvIcon}
                    onClick={() => exportRecords('csv')}
                    style={toolbarButtonStyle}
                    className='alarm-action-btn'
                  />
                </div>
                <div>
                  <Button
                    onClick={() => exportRecords('pdf')}
                    themeColor={'primary'}
                    size="small"
                    title={localizationService.toLanguageString('export_to_pdf', 'NOT_FOUND')}
                    svgIcon={filePdfIcon}
                    style={toolbarButtonStyle}
                    className='alarm-action-btn'
                  />
                </div>
                <div>
                  <Button
                    title={localizationService.toLanguageString('export_to_excel', 'NOT_FOUND')}
                    themeColor={'primary'}
                    size="small"
                    svgIcon={fileExcelIcon}
                    onClick={() => exportRecords('excel')}
                    style={toolbarButtonStyle}
                    className='alarm-action-btn'
                  />
                </div>
              </div>
            </GridToolbar>

            {!props.preview && (
              <Column
                cells={{ data: (props) => <LinkMapCell {...props} /> }}
                width={50}
                editable={false}
                filterable={false}
                sortable={false}
              />
            )}

            {!props.preview && (
              <Column
                title={localizationService.toLanguageString('alarms.deactive', 'NOT_FOUND')}
                width={props.preview ? '0px' : 60}
                filterable={false}
                editable={false}
                sortable={false}
                cells={{ data: toggleAlarm }}
              />
            )}

            {
              columns?.length > 0 ?
                columns?.map((column: any, index: any) =>
                  column?.show && (
                    column.field == 'dateTime' ?
                      <Column
                        key={`alarms-col-${column.field ?? index}`}
                        field='dateTime'
                        title={localizationService.toLanguageString('alarms.dateTime', 'NOT_FOUND')}
                        editable={false}
                        format={column.format}
                        columnMenu={(props) => (
                          <GridColumnMenuFilter
                            {...props}
                            alwaysExpand
                            filterUI={CustomGridFilterDateTime}
                            onExpandChange={() => { }}
                            onCloseMenu={() => {
                              localStorage.removeItem('filterOpenedColumn');
                            }}
                          />
                        )}
                        filter={'date'}
                        sortable={true}
                        cells={{
                          data: (props) => <DateTimeCellWithBackGroundComponent {...props} />,
                          headerCell: IconHeaderCell
                        }}
                      />
                      :
                      column.field == 'info' ?
                        <Column
                          key={`alarms-col-${column.field ?? index}`}
                          field={column.field}
                          title={column.title}
                          filterable={column?.filterable}
                          sortable={false}
                          className='k-text-center'
                          cells={{
                            data: (props) => <InfoTagAlarmComponent {...props} />,
                            headerCell: IconHeaderCell
                          }}
                          filter={column?.filter}
                          format={column?.format}
                          editable={false}
                        />
                        :
                        <Column
                          key={`alarms-col-${column.field ?? index}`}
                          field={column.field}
                          title={column.title}
                          filterable={column?.filterable}
                          columnMenu={ColumnMenuCheckboxFilter}
                          className='k-text-center'
                          cells={{ headerCell: IconHeaderCell }}
                          filter={column?.filter}
                          format={column?.format}
                          width={'2000'}
                          editable={false}
                        />
                  )
                )
                :
                columns?.columns?.map((column: any, index: any) =>
                  column.show && !props.preview && (
                    <Column
                      key={`alarms-fallback-col-${column.field ?? index}`}
                      field={column.field}
                      title={column.title}
                      filterable={column?.filterable}
                      columnMenu={ColumnMenuCheckboxFilter}
                      className='k-text-center'
                      cells={{ data: (props) => <InfoTagAlarmComponent {...props} /> }}
                      filter={column?.filter}
                      format={column?.format}
                      editable={false}
                    />
                  )
                )
            }
          </Grid>
          {/*</div>
          </ExcelExport>*/}
        </>
      }

      <GridPDFExport margin="1cm">
        {
          columns?.length > 0 ?
            columns?.map((column: any, index: any) =>
              column.show && (
                <Column
                  key={`alarms-pdf-col-${column.field ?? index}`}
                  field={column.field}
                  title={column.title}
                  filterable={column?.filterable}
                  columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                  className='k-text-center'
                  cells={{ data: (props) => <InfoTagAlarmComponent  {...props} /> }}
                  filter={column?.filter}
                  format={column?.format}
                  editable={column?.editable}
                />
              )
            )
            :
            columns?.columns?.map((column: any, index: any) =>
              column.show && (
                <Column
                  key={`alarms-pdf-fallback-col-${column.field ?? index}`}
                  field={column.field}
                  title={column.title}
                  filterable={column?.filterable}
                  columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                  className='k-text-center'
                  cells={{ data: (props) => <InfoTagAlarmComponent  {...props} /> }}
                  filter={column?.filter}
                  format={column?.format}
                  editable={column?.editable}
                />
              )
            )
        }
        <Grid
          className={`alarm-grid-colorized${hasActiveGridFilters ? ' grid-filter-active' : ''}`}
          data={processedData}
          dataItemKey={DATA_ITEM_KEY}
          //cellRender={cellRender}
          cells={{ data: CellRender }}
          //total={totalItems}
          take={dataState?.take ?? 10}
          skip={dataState?.skip ?? 0}
          pageable={{
            type: 'input',
            buttonCount: 5,
            pageSizes: [5, 10, 50, 100],
            info: true
          }}
          sortable={true}
          filterable={true}
          //reorderable={false}
          sort={dataState.sort}
          filter={dataState.filter}
          onSelectionChange={onSelectedItemsChange}
          onHeaderSelectionChange={onHeaderSelection}
          onPageChange={onPageChange}
          onFilterChange={onFilterChange}
          onColumnReorder={onColumnReorder}
          onColumnResize={onColumnResize}
          onDataStateChange={onDataStateChange}
          scrollable={'none'}
        >
          <GridToolbar style={toolbarStyle} className='alarm-animated-toolbar alarm-animated-toolbar-secondary'>
            <div>
                        <h3 className="page-title">{localizationService.toLanguageString('alarms.title', 'NOT_FOUND')}</h3>
            </div>
            {!props.preview ?
              <div>
                <button
                  className='alarm-page disable-alarm-button'
                  onClick={() => {
                    onHeaderSelection(alarms);
                    setDisableAllAlarms(true)
                  }}
                >
                  {localizationService.toLanguageString('alarms.disable_all_alarms_button', 'NOT_FOUND')}
                </button>
              </div>
              : null}
            <Popup
              anchor={anchor.current && anchor.current.element}
              show={showPopupSaveConfig}
              popupClass={"popup-content-save-synoptic-config"}
            >
              {localizationService.toLanguageString('synoptic_devices.saved_config', 'NOT_FOUND')}
            </Popup>
            <div style={toolbarActionsStyle} className='alarm-toolbar-actions'>
              {!props.preview ?
                <div>
                  <Button
                    ref={anchorSave}
                    title={localizationService.toLanguageString('save_config', 'NOT_FOUND')}
                    themeColor={'primary'}
                    size="small"
                    svgIcon={saveIcon}
                    style={toolbarButtonStyle}
                    className='alarm-action-btn'
                    onClick={() => {
                      saveStateToLocalStorage();
                      onClickSaveConfig()
                    }}
                  >
                  </Button>
                </div>
                : null}
              {!props.preview ?
                <div>
                  <Button
                    themeColor={'primary'}
                    size="small"
                    svgIcon={uploadIcon}
                    style={toolbarButtonStyle}
                    className='alarm-action-btn'
                    onClick={loadStateFromLocalStorage}
                    title={localizationService.toLanguageString('load_config', 'NOT_FOUND')}
                  >
                  </Button>
                </div>
                : null}
              <div>
                <CSVLink data={json2csv(alarms)} separator={","}>
                  <Button
                    themeColor={"primary"}
                    size="small"
                    title={localizationService.toLanguageString('export_to_csv', 'NOT_FOUND')}
                    svgIcon={fileCsvIcon}
                    style={toolbarButtonStyle}
                    className='alarm-action-btn'
                  />
                </CSVLink>
              </div>
              <div>
                <Button
                  onClick={() => exportRecords('pdf')}
                  themeColor={"primary"}
                  size="small"
                  title={localizationService.toLanguageString('export_to_pdf', 'NOT_FOUND')}
                  svgIcon={filePdfIcon}
                  style={toolbarButtonStyle}
                  className='alarm-action-btn'
                />
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('export_to_excel', 'NOT_FOUND')}
                  themeColor={"primary"}
                  size="small"
                  svgIcon={fileExcelIcon}
                  onClick={() => exportRecords('excel')}
                  style={toolbarButtonStyle}
                  className='alarm-action-btn'
                />
              </div>
              <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />
            </div>
          </GridToolbar>
          {!props.preview ?
            <Column
              cells={{ data: (props) => <LinkMapCell {...props} /> }}
              width={50}
              editable={false}
              filterable={false}
              sortable={false}
            />
            : null}
          {!props.preview ?
            <Column
              title={localizationService.toLanguageString('alarms.deactive', 'NOT_FOUND')}
              width={props.preview ? '0px' : 100}
              filterable={false}
              editable={false}
              cells={{ data: toggleAlarm }}
            />
            : null}
          {
            columns?.length > 0 ?
              columns?.map((column: any, index: any) =>
                column.show && (
                  <GridColumn
                    key={`alarms-grid-col-${column.field ?? index}`}
                    field={column.field}
                    title={column.title}
                    filterable={column?.filterable}
                    columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                    className='k-text-center'
                    cells={{ data: (props) => <InfoTagAlarmComponent  {...props} /> }}
                    filter={column?.filter}
                    format={column?.format}
                    editable={column?.editable}
                  />
                )
              )
              :
              columns?.columns?.map((column: any, index: any) =>
                column.show && (
                  <GridColumn
                    key={`alarms-grid-fallback-col-${column.field ?? index}`}
                    field={column.field}
                    title={column.title}
                    filterable={column?.filterable}
                    columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                    className='k-text-center'
                    cells={{ data: (props) => <InfoTagAlarmComponent  {...props} /> }}
                    filter={column?.filter}
                    format={column?.format}
                    editable={column?.editable}
                  />
                )
              )
          }
        </Grid>
      </GridPDFExport>

      {showPopupInfo &&
        <Window
          title={localizationService.toLanguageString('alarms.alarm_details', 'NOT_FOUND')}
          onClose={() => setShowPopupInfo(!showPopupInfo)}
          resizable={false}
          initialWidth={400}
          initialHeight={480}
          style={{
            border: '2px solid white',
            background: '#198792',
            color: 'white',
            boxShadow: '0px 0px 0px 2px #198792'
          }}
        >
          <div>
            <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.deviceMac', 'NOT_FOUND')}</span>: {alarmInfo?.deviceMac ? alarmInfo?.deviceMac : "N/D"}</p>
            <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.eventType.eventTypeName', 'NOT_FOUND')}</span>: {alarmInfo?.eventType?.eventTypeName ? alarmInfo?.eventType?.eventTypeName : "N/D"}</p>
            <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.firmware', 'NOT_FOUND')}</span>: {(alarmInfo?.info?.firmware != null && alarmInfo?.info?.firmware != '') ? alarmInfo?.info?.firmware : "N/D"}</p>
            <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.temperature', 'NOT_FOUND')}</span>: {alarmInfo?.info?.temperature != null ? alarmInfo?.info?.temperature : "N/D"}</p>
            <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.pressure', 'NOT_FOUND')}</span>: {alarmInfo?.info?.pressure != null ? alarmInfo?.info?.pressure : "N/D"}</p>
            <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.battery', 'NOT_FOUND')}</span>: {alarmInfo?.info?.battery != null ? alarmInfo?.info?.battery : "N/D"}</p>
            <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.longitude', 'NOT_FOUND')}</span>: {alarmInfo?.info?.position?.longitude != null ? alarmInfo?.info?.position?.longitude : "N/D"}</p>
            <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.latitude', 'NOT_FOUND')}</span>: {alarmInfo?.info?.position?.latitude != null ? alarmInfo?.info?.position?.latitude : "N/D"}</p>
            <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.altitude', 'NOT_FOUND')}</span>: {alarmInfo?.info?.position?.elevation != null ? alarmInfo?.info?.position?.elevation : "N/D"}</p>
          </div>
        </Window>
      }
      {props.showPopupAlarm && (
        <Dialog
          className="confirm-action-dialog"
          title={disableAllAlarms ? localizationService.toLanguageString('alarms.disable_all_alarms', 'NOT_FOUND') : localizationService.toLanguageString('alarms.disable_alarm', 'NOT_FOUND')}
          onClose={props.handlePopupAlarm}
        >
          <div className="dialog-actions">
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              onClick={props.handlePopupAlarm}
            >
              {localizationService.toLanguageString('no', 'NOT_FOUND')}
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
              onClick={disableAlarm}
            >
              {localizationService.toLanguageString('alarms.yes', 'NOT_FOUND')}
            </button>
          </div>
        </Dialog>
      )}

    </div>
  );
};